import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  BoxProps,
  CircularProgress,
  forwardRef,
  HStack,
  Icon,
  VStack,
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import iconInfoFilled from '~/assets/svgs/icon-info-filled.svg?react'
import {
  MangoAlertVariant,
  useMangoAlertStyles,
} from '~/components/shared/componentStyles/MangoAlert.style'
import { MangoCloseButtonVariant } from '~/components/shared/componentStyles/MangoCloseButton.style'
import { MangoCloseButton } from '~/components/shared/MangoCloseButton'
import { NotificationDisposition } from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { Size } from '~/types/ThemeTypes'

type MangoAlertProps = BoxProps & {
  title?: string
  status: NotificationDisposition
  description?: string | React.ReactNode
  showCloseButton?: boolean
  closeOnClick?: boolean
  onClose?: () => void
}

const getVariantFromStatus = (status: NotificationDisposition) => {
  switch (status) {
    case NotificationDisposition.Success:
      return MangoAlertVariant.Success
    case NotificationDisposition.Error:
      return MangoAlertVariant.Error
    case NotificationDisposition.Warning:
      return MangoAlertVariant.Warning
    case NotificationDisposition.Info:
      return MangoAlertVariant.Info
    case NotificationDisposition.Loading:
      return MangoAlertVariant.Loading
    default:
      return MangoAlertVariant.Info
  }
}

export const MangoAlert = forwardRef<MangoAlertProps, 'div'>(
  (props: MangoAlertProps, ref) => {
    const {
      status,
      description,
      title,
      showCloseButton,
      closeOnClick,
      onClose,
      ...rest
    } = props

    const variant = useMemo(() => getVariantFromStatus(status), [status])
    const styles = useMangoAlertStyles({ variant })

    const icon = useMemo(() => {
      switch (status) {
        case NotificationDisposition.Success:
          return <Icon as={CheckCircleIcon} sx={styles.icon} />
        case NotificationDisposition.Error:
        case NotificationDisposition.Warning:
          return <Icon as={WarningIcon} sx={styles.icon} />
        case NotificationDisposition.Info:
          return <Icon as={iconInfoFilled} sx={styles.icon} />
        case NotificationDisposition.Loading:
          return (
            <CircularProgress
              isIndeterminate
              color={styles.circleProgress.color!.toString()}
              trackColor={styles.circleProgress.backgroundColor!.toString()}
              size={styles.circleProgress.height!.toString()}
              thickness={styles.circleProgress.width!.toString()}
            />
          )
        default:
          return <></>
      }
    }, [status])

    return (
      <Box
        ref={ref}
        as={Alert}
        __css={styles.container}
        onClick={closeOnClick ? onClose : undefined}
        cursor={closeOnClick ? 'pointer' : undefined}
        {...rest}
      >
        <HStack sx={styles.contentContainer}>
          {icon}
          <VStack sx={styles.textContainer}>
            {title && <AlertTitle sx={styles.title}>{title}</AlertTitle>}
            {description && (
              <AlertDescription sx={styles.description}>
                {description}
              </AlertDescription>
            )}
          </VStack>
        </HStack>
        {showCloseButton && (
          <Box
            as={MangoCloseButton}
            sx={styles.closeButton}
            onClick={onClose}
            size={Size.sm}
            variant={MangoCloseButtonVariant.AlertWindow}
          />
        )}
      </Box>
    )
  },
)
